import React, { createContext, useContext, useState, useCallback } from "react";
import translations from "./translations";
import { useAppConfig } from "../context/AppConfigContext";

const LanguageContext = createContext();

function detectLanguage() {
  // Always respect user's explicit choice stored in localStorage
  const stored = localStorage.getItem("app_language");
  if (stored && translations[stored]) return stored;

  const browserLang = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  if (browserLang.startsWith("es")) return "es";
  return "en";
}

export function LanguageProvider({ children, enabled }) {
  const { orgLabels, cardLabels } = useAppConfig();

  const [language, setLanguageState] = useState(() => {
    // Always respect user's explicit localStorage choice on init
    const stored = localStorage.getItem("app_language");
    if (stored && translations[stored]) return stored;
    return detectLanguage();
  });

  const setLanguage = useCallback((lang) => {
    localStorage.setItem("app_language", lang);
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (key, params) => {
      const str = translations[language]?.[key] || translations.en[key] || key;
      const labels = orgLabels?.[language] || orgLabels?.en || { singular: "Organization", plural: "Organizations" };
      const cLabels = cardLabels?.[language] || cardLabels?.en || { singular: "Card", plural: "Cards" };
      const isFeminine = labels.gender === "feminine";
      const merged = {
        orgLabel: labels.singular,
        orgLabelPlural: labels.plural,
        orgArticle: language === "es" ? (isFeminine ? "esta" : "este") : "this",
        orgArticleUn: language === "es" ? (isFeminine ? "una" : "un") : "a",
        cardLabel: cLabels.singular,
        cardLabelPlural: cLabels.plural,
        ...params,
      };
      return str.replace(/\{(\w+)\}/g, (_, k) => merged[k] ?? `{${k}}`);
    },
    [language, orgLabels, cardLabels]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, i18nEnabled: enabled }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useTranslation = () => useContext(LanguageContext);
